import type { JobExecutionStatus } from '@cdw-ae/common-data';
import { createAction, props } from '@ngrx/store';

// Initialization
export const initJobsStatus = createAction('[Jobs Status] Init');

// Workflow tracking (query by workflowName for background jobs like cache refresh)
// Use case: Monitor ALL executions of specific workflow types (e.g., full-asset-sync-workflow)
// Returns: Most recent execution per workflow for this customer
export const bindToFirestore = createAction(
  '[Jobs Status] Bind to Firestore',
  props<{ customerId: number }>()
);

export const unbindFromFirestore = createAction(
  '[Jobs Status] Unbind from Firestore'
);

// Firestore data updates (triggered by collection group query subscriptions)
// These actions update the store when Firestore collection group queries emit new data
// - activeExecutionsUpdated: Updates list of currently running workflow executions
// - finishedExecutionsUpdated: Updates list of most recent completed workflows (for "last updated" display)
export const activeExecutionsUpdated = createAction(
  '[Jobs Status/Firestore] Active Executions Updated',
  props<{ executions: JobExecutionStatus[] }>()
);

export const finishedExecutionsUpdated = createAction(
  '[Jobs Status/Firestore] Finished Executions Updated',
  props<{ executions: JobExecutionStatus[] }>()
);

export const bindingError = createAction(
  '[Jobs Status/Firestore] Binding Error',
  props<{ error: string }>()
);

// Tab-based job tracking
export const bindToMyJobs = createAction(
  '[Jobs Status] Bind to My Jobs',
  props<{ customerId: number; dbUserId: number }>()
);

export const myJobsUpdated = createAction(
  '[Jobs Status/Firestore] My Jobs Updated',
  props<{ executions: JobExecutionStatus[] }>()
);

export const bindToAllJobs = createAction(
  '[Jobs Status] Bind to All Jobs',
  props<{ customerId: number }>()
);

export const allJobsUpdated = createAction(
  '[Jobs Status/Firestore] All Jobs Updated',
  props<{ executions: JobExecutionStatus[] }>()
);

export const setActiveTab = createAction(
  '[Jobs Status] Set Active Tab',
  props<{ tab: 'my' | 'all' }>()
);

// Load completed jobs from SQL (with Redis caching)
export const loadUserExecutions = createAction(
  '[Jobs Status] Load User Executions',
  props<{ customerId: number; dbUserId: number; limit?: number }>()
);

export const userExecutionsLoaded = createAction(
  '[Jobs Status] User Executions Loaded',
  props<{ executions: JobExecutionStatus[] }>()
);

export const userExecutionsError = createAction(
  '[Jobs Status] User Executions Error',
  props<{ error: string }>()
);

export const loadAllExecutions = createAction(
  '[Jobs Status] Load All Executions',
  props<{ customerId: number; limit?: number }>()
);

export const allExecutionsLoaded = createAction(
  '[Jobs Status] All Executions Loaded',
  props<{ executions: JobExecutionStatus[] }>()
);

export const allExecutionsError = createAction(
  '[Jobs Status] All Executions Error',
  props<{ error: string }>()
);

// Global job completion — dispatched when a job disappears from Firestore (completed or errored)
export const jobCompleted = createAction(
  '[Jobs Status] Job Completed',
  props<{ execution: JobExecutionStatus }>()
);

// Export download — triggers authenticated download via backend
export const downloadExport = createAction(
  '[Jobs Status] Download Export',
  props<{ jobId: number; customerId: number }>()
);

// Load workflow-specific executions from SQL (for cache refresh status, etc.)
export const loadWorkflowExecutions = createAction(
  '[Jobs Status] Load Workflow Executions',
  props<{ customerId: number; workflowName: string; limit?: number }>()
);

export const workflowExecutionsLoaded = createAction(
  '[Jobs Status] Workflow Executions Loaded',
  props<{ workflowName: string; executions: JobExecutionStatus[] }>()
);

export const workflowExecutionsError = createAction(
  '[Jobs Status] Workflow Executions Error',
  props<{ workflowName: string; error: string }>()
);

// ---------------------------------------------------------------------------
// SSRM grid selection-lock failsafe (fix/SSRM-row-activity-ui)
//
// The selection lock freezes the user's grid selection while a bulk job runs.
// Lock state is derived from `myJobs` filtered by gridSessionUuid (server-side
// truth) PLUS `hydratedLocks` (pessimistic state from sessionStorage to cover
// the brief window between page render and Firestore listener bind) MINUS
// `releasedLockSessions` (optimistic local clears that haven't yet propagated
// through Firestore terminal-state delete).
// ---------------------------------------------------------------------------

/**
 * Dispatched by the device-search consumer immediately after a bulk-action
 * enqueue resolves with a jobId. Triggers sessionStorage persistence so the
 * lock survives same-tab refresh.
 */
export const bulkJobEnqueued = createAction(
  '[Jobs Status/Lock] Bulk Job Enqueued',
  props<{ jobId: number; gridSessionUuid: string; selectedCount: number }>()
);

/**
 * Dispatched synchronously on app init (before Firestore listener binds) when
 * a lock entry is found in sessionStorage for the current grid-session. Sets
 * the pessimistic lock so checkboxes are disabled instantly on render.
 * Released when either (a) Firestore confirms no matching active job exists
 * (quietReconcile path), or (b) the user clicks Cancel on the banner.
 */
export const lockHydratedFromStorage = createAction(
  '[Jobs Status/Lock] Lock Hydrated From Storage',
  props<{
    gridSessionUuid: string;
    jobId: number;
    selectedCount: number;
    lockSetAt: number;
  }>()
);

/**
 * Triggered by the visibility/online effect or the threshold timer. The
 * cancelJob$ effect performs a SQL re-fetch and either quietly releases the
 * lock (job actually finished) or surfaces the stuck-job banner (job genuinely
 * still in flight beyond its formula budget).
 */
export const reconcileStuckJobs = createAction(
  '[Jobs Status/Lock] Reconcile Stuck Jobs'
);

/**
 * User-initiated cancel from the stuck-job banner OR from the JobCardComponent
 * cancel button. Optimistically dispatches selectionLockReleaseRequested
 * BEFORE the HTTP call resolves so the lock UI clears instantly; the API call
 * is fire-and-confirm with snackbars on success/failure.
 */
export const cancelJobRequested = createAction(
  '[Jobs Status/Lock] Cancel Job Requested',
  props<{ jobId: number; customerId: number; gridSessionUuid?: string }>()
);

export const cancelJobSucceeded = createAction(
  '[Jobs Status/Lock] Cancel Job Succeeded',
  props<{ jobId: number }>()
);

export const cancelJobFailed = createAction(
  '[Jobs Status/Lock] Cancel Job Failed',
  props<{ jobId: number; error: string }>()
);

/**
 * Optimistic lock-release request. Dispatched only by `cancelJob$` when the
 * user clicks Cancel on the stuck-job banner or job card. Marks the session
 * as released so the selector trump-cards future Firestore ticks during the
 * window between the click and the eventual terminal-state propagation —
 * but leaves `hydratedLocks[gridSessionUuid]` intact so the terminal-state
 * effect can still identify the session by `jobId` (essential for the
 * fallback path, where `additionalJobData.gridSessionUuid` is missing).
 *
 * Pairs with `selectionLockReleaseConfirmed` (terminal-state) the same way
 * `cancelJobRequested` pairs with `cancelJobSucceeded` — optimistic event
 * first, server-confirmed event second.
 */
export const selectionLockReleaseRequested = createAction(
  '[Jobs Status/Lock] Selection Lock Release Requested',
  props<{ gridSessionUuid: string }>()
);

/**
 * Terminal-state confirmation for a previously-active lock. Dispatched only
 * by `releaseLockOnJobCompleted$` after Firestore confirms a job's terminal
 * state (either via `additionalJobData.gridSessionUuid` on the primary path
 * or via `jobId` match against `hydratedLocks` on the fallback path).
 *
 * The reducer drops `hydratedLocks[gridSessionUuid]` AND filters the entry
 * out of `releasedLockSessions`, atomically — this is the only place
 * `releasedLockSessions` is cleaned, and it works regardless of whether the
 * session entered `releasedLockSessions` via an earlier optimistic cancel
 * or never entered it at all (natural completion).
 */
export const selectionLockReleaseConfirmed = createAction(
  '[Jobs Status/Lock] Selection Lock Release Confirmed',
  props<{ gridSessionUuid: string }>()
);

/**
 * Shape of the sessionStorage payload written by `persistLockToSessionStorage$`
 * and read back on hydration. Centralized here so all writers/readers can
 * import a single type rather than re-declaring the shape.
 */
export interface SsrmLockStoragePayload {
  jobId: number;
  selectedCount: number;
  lockSetAt: number;
}

/**
 * Build the sessionStorage key for a grid session's lock entry. Centralized
 * so the storage-write site, the storage-clear site, and the hydration site
 * cannot drift — stranded entries (the bug EDUS-2528 was hunting) are
 * impossible when there's only one source of truth for the key shape.
 *
 * Parallel to the `grid-session:{viewKey}` convention minted in
 * `CdwSsrmGridComponent.mintOrRestoreSessionId`.
 */
export function buildSsrmLockStorageKey(gridSessionUuid: string): string {
  return `ssrm-lock:${gridSessionUuid}`;
}

/**
 * Periodic clock tick. Drives the `selectStuckJobsForGridSession` selector's
 * threshold computation without making it impure. Fired every 30s by the
 * nowTick$ effect while any in-flight bulk job exists.
 */
export const nowTick = createAction(
  '[Jobs Status/Lock] Now Tick',
  props<{ nowMs: number }>()
);
